/**
 * El boton de "Actualizar": cuando se publica una version nueva, decirlo una
 * vez, en el marco, y estrenarla con un toque.
 *
 * POR QUE HACE FALTA. La navegacion va red primero (`public/sw.js`), asi que
 * ABRIR la app siempre trae la ultima version. El problema es la que nunca se
 * abre otra vez: la computadora del mostrador con la pestaña abierta todo el
 * dia, el telefono que solo se manda al fondo. Esas se quedaban con la version
 * de la mañana, sin nada que lo dijera, y lo que se acababa de arreglar "no
 * servia".
 *
 * COMO SE ENTERA. Le pregunta al servidor si `sw.js` cambio
 * (`registration.update()`) cada 15 minutos con la app a la vista y en cuanto
 * vuelve a estar a la vista —que es cuando el telefono sale del fondo—. Cada
 * publicacion cambia `sw.js` porque la compilacion le sella una huella
 * (`lib/build-stamp.ts`); el service worker nuevo se instala y se queda
 * ESPERANDO, sin tomar el control, hasta que la app se lo pida.
 *
 * LO QUE LO HACE INTELIGENTE: compara huellas antes de abrir la boca. La pagina
 * sabe de que publicacion es (`<meta name="app-build">`) y el service worker
 * que espera lo dice si se le pregunta. Si son la misma, esta pantalla YA es la
 * version nueva —la abrieron despues de publicar, que es lo comun— y solo falta
 * estrenar su service worker, cosa que se hace callando: no cambia nada a la
 * vista. Si son distintas, la pantalla es vieja, y entonces si sale el boton.
 * Sin esa comparacion el boton saldria cada vez que alguien abre la app despues
 * de una publicacion, pidiendo actualizar lo que ya esta actualizado, y un
 * aviso que casi siempre sobra se deja de leer.
 *
 * NUNCA RECARGA POR SU CUENTA, con una excepcion. En el mostrador la pantalla
 * puede estar a media venta con el cliente enfrente: actualizar es de quien la
 * usa, cuando le convenga. La excepcion es la pantalla que ya no puede abrir
 * —ver `recoverFromStaleChunks`—, porque ahi recargar no interrumpe nada.
 */

import { useSyncExternalStore } from 'react';
import { BUILD_META_NAME, UNSTAMPED_BUILD } from './build-stamp.js';

/** Cada cuanto se pregunta al servidor si hay version nueva, con la app a la vista. */
const CHECK_EVERY_MS = 15 * 60_000;
/** Volver a la app pregunta en el acto, pero no mas de una vez por minuto. */
const MIN_CHECK_GAP_MS = 60_000;
/** Cuanto se espera a que un service worker diga su huella. */
const ASK_TIMEOUT_MS = 3_000;
/** Si el service worker nuevo no toma el control en este tiempo, se recarga igual. */
const TAKEOVER_GRACE_MS = 3_000;
/** Dos recargas por un activo perdido en menos de esto es un bucle, no una version nueva. */
const STALE_RELOAD_GUARD_MS = 30_000;
const STALE_RELOAD_KEY = 'abarrotes.update.reload';

/** Los dos mensajes que entiende `public/sw.js`. */
const SKIP_WAITING = 'skip-waiting';
const ASK_BUILD = 'build';

export type UpdateStatus = 'idle' | 'ready' | 'applying';

let status: UpdateStatus = 'idle';
let registration: ServiceWorkerRegistration | null = null;
/** La huella de lo que se esta ofreciendo; `null` si el service worker no la dijo. */
let offered: string | null = null;
/** Lo que alguien descarto con "Ahora no". No se vuelve a ofrecer; una version mas nueva, si. */
let dismissed: string | null = null;
let lastCheck = 0;
let reloading = false;
const listeners = new Set<() => void>();

function setStatus(next: UpdateStatus): void {
  if (next === status) return;
  status = next;
  for (const listener of listeners) listener();
}

/** Con la que se compara lo descartado: una huella desconocida es una sola clave. */
const offerKey = (build: string | null): string => build ?? '?';

function pageBuild(): string {
  return document.querySelector<HTMLMetaElement>(`meta[name="${BUILD_META_NAME}"]`)?.content ?? UNSTAMPED_BUILD;
}

/** Le pregunta su huella a un service worker. `null` si no contesta a tiempo. */
function askBuild(worker: ServiceWorker): Promise<string | null> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const finish = (build: string | null): void => {
      window.clearTimeout(timer);
      channel.port1.close();
      resolve(build);
    };
    const timer = window.setTimeout(() => finish(null), ASK_TIMEOUT_MS);
    channel.port1.onmessage = (event: MessageEvent<unknown>) => finish(typeof event.data === 'string' ? event.data : null);
    try {
      worker.postMessage(ASK_BUILD, [channel.port2]);
    } catch {
      finish(null);
    }
  });
}

function offer(build: string | null): void {
  offered = build;
  if (status === 'applying' || dismissed === offerKey(build)) return;
  setStatus('ready');
}

/**
 * La pantalla ya es de la version mas nueva que se conoce: lo que se estuviera
 * ofreciendo era de una anterior —un service worker que se quedo esperando
 * desde antes de la ultima publicacion— y ya no hay nada que actualizar.
 */
function upToDate(): void {
  offered = null;
  if (status === 'ready') setStatus('idle');
}

/** Un service worker nuevo termino de instalarse y espera. */
async function consider(worker: ServiceWorker): Promise<void> {
  const build = await askBuild(worker);
  // Mientras contestaba pudo activarse solo —primera instalacion, sin version
  // anterior— o llegar otro mas nuevo que lo reemplazo: entonces ya no es asunto.
  if (worker !== registration?.waiting) return;
  if (build !== null && build === pageBuild()) {
    upToDate();
    worker.postMessage(SKIP_WAITING);
    return;
  }
  offer(build);
}

/**
 * Cambio el service worker sin que esta pestaña lo pidiera: lo estreno otra
 * pestaña, o esta misma callando (arriba). Si la pantalla quedo atras de la
 * version que ahora la controla, se ofrece; aqui no hay nada que activar, asi
 * que "Actualizar" solo recarga.
 */
async function onControllerChange(): Promise<void> {
  if (status === 'applying') return;
  const controller = navigator.serviceWorker.controller;
  if (!controller) return;
  const build = await askBuild(controller);
  if (build !== null && build === pageBuild()) {
    upToDate();
    return;
  }
  offer(build);
}

function check(): void {
  if (!registration || document.visibilityState !== 'visible' || navigator.onLine === false) return;
  const now = Date.now();
  if (now - lastCheck < MIN_CHECK_GAP_MS) return;
  lastCheck = now;
  registration.update().catch(() => {
    // Sin red o con el servidor caido no hay nada que avisar: pregunta la siguiente vuelta.
  });
}

/** Se llama una vez, con el registro del service worker. */
export function watchForUpdates(reg: ServiceWorkerRegistration): void {
  registration = reg;
  // La navegacion que acaba de abrir esta pantalla ya le pregunto al servidor.
  lastCheck = Date.now();

  const track = (worker: ServiceWorker | null): void => {
    worker?.addEventListener('statechange', () => {
      if (worker.state === 'installed') void consider(worker);
    });
  };
  if (reg.waiting) void consider(reg.waiting);
  track(reg.installing);
  reg.addEventListener('updatefound', () => track(reg.installing));

  navigator.serviceWorker.addEventListener('controllerchange', () => void onControllerChange());
  window.setInterval(check, CHECK_EVERY_MS);
  document.addEventListener('visibilitychange', check);
}

function reload(): void {
  if (reloading) return;
  reloading = true;
  window.location.reload();
}

/**
 * Estrena la version nueva: le pide al service worker que espera que tome el
 * control y recarga cuando lo tome. La recarga trae la pagina nueva porque la
 * navegacion va red primero, y lo que se estaba capturando sobrevive porque
 * vive en un borrador (`lib/draft.tsx`), no en la pantalla.
 *
 * Si el service worker no contesta en `TAKEOVER_GRACE_MS`, se recarga igual: la
 * pagina nueva llega de todos modos y, ya sin pantallas viejas abiertas, el
 * navegador lo activa solo.
 */
export function applyUpdate(): void {
  if (status === 'applying') return;
  setStatus('applying');
  const waiting = registration?.waiting ?? null;
  if (!waiting) {
    reload();
    return;
  }
  navigator.serviceWorker.addEventListener('controllerchange', reload, { once: true });
  window.setTimeout(reload, TAKEOVER_GRACE_MS);
  waiting.postMessage(SKIP_WAITING);
}

export function dismissUpdate(): void {
  if (status !== 'ready') return;
  dismissed = offerKey(offered);
  setStatus('idle');
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getUpdateStatus(): UpdateStatus {
  return status;
}

export function useUpdateStatus(): UpdateStatus {
  return useSyncExternalStore(subscribe, getUpdateStatus);
}

/**
 * La pantalla que ya no puede abrir porque la publicacion se llevo su codigo.
 *
 * Cada pantalla se baja la primera vez que se abre, de un archivo con hash en
 * el nombre, y una publicacion reemplaza esos archivos. Asi que una pestaña
 * abierta desde antes de publicar, al tocar una pantalla que todavia no habia
 * abierto, pide un archivo que ya no existe: Vite avisa con
 * `vite:preloadError` y la pantalla no carga. Recargar trae la version nueva
 * completa, y aqui no interrumpe nada, porque lo que se iba a ver no se puede
 * ver.
 *
 * Dos guardas: sin red no se recarga —el archivo no falto, falto la conexion, y
 * recargar sin red solo serviria la misma version guardada—; y no se recarga
 * dos veces en `STALE_RELOAD_GUARD_MS`, porque si el archivo falta tambien
 * despues de recargar no es una version vieja, es otra cosa, y recargar en
 * bucle dejaria la pantalla parpadeando sin poder leer nada.
 */
export function recoverFromStaleChunks(): void {
  window.addEventListener('vite:preloadError', () => {
    if (navigator.onLine === false) return;
    const now = Date.now();
    try {
      if (now - Number(sessionStorage.getItem(STALE_RELOAD_KEY) ?? 0) < STALE_RELOAD_GUARD_MS) return;
      sessionStorage.setItem(STALE_RELOAD_KEY, String(now));
    } catch {
      // Sin donde anotar la recarga no hay guarda contra el bucle: mejor no recargar.
      return;
    }
    applyUpdate();
  });
}
